import { appConfig } from '../config/appConfig';
import { assetService } from './assetService';
import { apiClient } from './apiClient';
import { isAssetExtractionActive, isAssetExtractionFailed } from '../utils/assetDisplay';

export type AssetParticleExtractionLiveEventStatus = 'processing' | 'completed' | 'failed';

export type AssetParticleExtractionLiveEvent = {
  kind: 'asset_particle_extraction_state_changed';
  assetId: string;
  userId: string;
  status: AssetParticleExtractionLiveEventStatus;
  taskId?: string | null;
  savedCount?: number | null;
  error?: string | null;
  emittedAt: string;
};

export type LiveUpdateEvent = AssetParticleExtractionLiveEvent;
type LiveUpdateListener = (event: LiveUpdateEvent) => void;

export type ExtractionWatchInput = {
  assetId: string;
  taskId?: string | null;
  lockExpiresAt?: string | null;
  userId?: string | null;
};

type ExtractionJobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';

type ExtractionWatcher = {
  assetId: string;
  taskId: string | null;
  userId: string | null;
  startedAt: number;
  expiresAt: number;
  consecutiveFailures: number;
  emittedProcessing: boolean;
};

const DEFAULT_POLL_INTERVAL_MS = 90_000;
const MIN_POLL_INTERVAL_MS = 45_000;
const MAX_POLL_INTERVAL_MS = 180_000;
const MAX_WATCH_MS = 20 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

function isJobLookupMiss(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /particle_extraction_job_not_found|job_not_found|404|not found/i.test(message);
}

function resolvePollIntervalMs(): number {
  const configured = Number(import.meta.env.VITE_LIVE_UPDATES_POLL_MS);
  if (Number.isFinite(configured) && configured >= MIN_POLL_INTERVAL_MS) {
    return Math.min(configured, MAX_POLL_INTERVAL_MS);
  }
  return DEFAULT_POLL_INTERVAL_MS;
}

function resolveWatchExpiresAt(lockExpiresAt: string | null | undefined, startedAt: number): number {
  const policyLimit = startedAt + MAX_WATCH_MS;
  const lockLimit = lockExpiresAt ? Date.parse(lockExpiresAt) : Number.NaN;
  if (Number.isFinite(lockLimit)) {
    return Math.min(lockLimit, policyLimit);
  }
  return policyLimit;
}

function readSavedCount(result: Record<string, unknown> | null | undefined): number | null {
  if (!result) {
    return null;
  }
  const direct = result.savedCount;
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct;
  }
  const relationRecord = result.relationRecord;
  if (relationRecord && typeof relationRecord === 'object' && !Array.isArray(relationRecord)) {
    const particlesCreated = (relationRecord as Record<string, unknown>).particlesCreated;
    if (typeof particlesCreated === 'number' && Number.isFinite(particlesCreated)) {
      return particlesCreated;
    }
    const particleIds = (relationRecord as Record<string, unknown>).particleIds;
    if (Array.isArray(particleIds)) {
      return particleIds.length;
    }
  }
  return null;
}

class LiveUpdatesService {
  private listeners = new Set<LiveUpdateListener>();
  private watchers = new Map<string, ExtractionWatcher>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimerGeneration = 0;
  private eventSource: EventSource | null = null;
  private polling = false;
  private recentEventSignatures: string[] = [];

  subscribe(listener: LiveUpdateListener): () => void {
    this.listeners.add(listener);
    void this.ensureSseIfConfigured();
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        this.stopSse();
      }
    };
  }

  /** @deprecated Use watchExtraction + subscribe instead. */
  start(_userId: string) {
    // no-op: global idle polling removed
  }

  stop() {
    this.stopPolling();
    this.stopSse();
    this.watchers.clear();
  }

  watchExtraction(input: ExtractionWatchInput) {
    const assetId = input.assetId.trim();
    if (!assetId) {
      return;
    }

    const startedAt = Date.now();
    const nextWatcher: ExtractionWatcher = {
      assetId,
      taskId: typeof input.taskId === 'string' && input.taskId.trim() ? input.taskId.trim() : null,
      userId: typeof input.userId === 'string' && input.userId.trim() ? input.userId.trim() : null,
      startedAt,
      expiresAt: resolveWatchExpiresAt(input.lockExpiresAt, startedAt),
      consecutiveFailures: 0,
      emittedProcessing: false,
    };

    const existing = this.watchers.get(assetId);
    if (existing) {
      nextWatcher.emittedProcessing = existing.emittedProcessing;
      if (nextWatcher.taskId && existing.taskId === nextWatcher.taskId) {
        nextWatcher.startedAt = existing.startedAt;
        nextWatcher.expiresAt = Math.max(existing.expiresAt, nextWatcher.expiresAt);
        nextWatcher.consecutiveFailures = 0;
      }
    }

    this.watchers.set(assetId, nextWatcher);
    if (!nextWatcher.emittedProcessing) {
      nextWatcher.emittedProcessing = true;
      this.emit({
        kind: 'asset_particle_extraction_state_changed',
        assetId,
        userId: nextWatcher.userId || '',
        status: 'processing',
        taskId: nextWatcher.taskId,
        emittedAt: new Date().toISOString(),
      });
    }

    this.ensurePolling();
    void this.pollActiveWatchers();
  }

  unwatch(assetId: string) {
    if (!this.watchers.delete(assetId.trim())) {
      return;
    }
    if (this.watchers.size === 0) {
      this.stopPolling();
    }
  }

  syncAssetExtractionWatchers(
    assets: Array<{
      id: string;
      particle_extraction_lock_task_id?: string | null;
      particle_extraction_lock_expires_at?: string | null;
      user_id?: string;
    }>,
  ) {
    const activeAssetIds = new Set<string>();
    for (const asset of assets) {
      if (!isAssetExtractionActive(asset)) {
        continue;
      }
      activeAssetIds.add(asset.id.trim());
      this.watchExtraction({
        assetId: asset.id,
        taskId: asset.particle_extraction_lock_task_id,
        lockExpiresAt: asset.particle_extraction_lock_expires_at,
        userId: asset.user_id,
      });
    }

    // This service is global across route changes. Reconcile removals as well
    // as additions, otherwise stale assets keep polling for up to 20 minutes.
    for (const assetId of [...this.watchers.keys()]) {
      if (!activeAssetIds.has(assetId)) {
        this.unwatch(assetId);
      }
    }
  }

  private emit(event: LiveUpdateEvent) {
    const signature = `${event.assetId}:${event.status}:${event.taskId || ''}:${event.emittedAt}`;
    if (this.recentEventSignatures.includes(signature)) {
      return;
    }

    this.recentEventSignatures.push(signature);
    if (this.recentEventSignatures.length > 64) {
      this.recentEventSignatures.shift();
    }

    for (const listener of this.listeners) {
      listener(event);
    }
  }

  private ensurePolling() {
    if (this.pollTimer || this.watchers.size === 0) {
      return;
    }

    const pollIntervalMs = resolvePollIntervalMs();
    const generation = ++this.pollTimerGeneration;
    this.pollTimer = setInterval(() => {
      if (generation !== this.pollTimerGeneration) {
        return;
      }
      void this.pollActiveWatchers();
    }, pollIntervalMs);
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.pollTimerGeneration += 1;
    this.polling = false;
  }

  private stopSse() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private async ensureSseIfConfigured() {
    if (appConfig.liveUpdatesMode !== 'sse' || this.eventSource) {
      return;
    }

    await apiClient.bootstrap();
    const userId = apiClient.getCurrentUser()?.id;
    const token = await apiClient.getAccessToken();
    if (!userId || !token) {
      return;
    }

    const url = new URL(`${appConfig.apiBaseUrl}/api/events`);
    url.searchParams.set('userId', userId);
    url.searchParams.set('access_token', token);
    this.eventSource = new EventSource(url.toString());
    this.eventSource.addEventListener('asset_particle_extraction_state_changed', (message) => {
      try {
        const raw = JSON.parse((message as MessageEvent).data) as Record<string, unknown>;
        const assetId = typeof raw.assetId === 'string' ? raw.assetId : '';
        const status = raw.status;
        if (!assetId || (status !== 'processing' && status !== 'completed' && status !== 'failed')) {
          return;
        }
        this.emit({
          kind: 'asset_particle_extraction_state_changed',
          assetId,
          userId: typeof raw.userId === 'string' ? raw.userId : '',
          status,
          taskId: typeof raw.taskId === 'string' ? raw.taskId : null,
          savedCount: typeof raw.savedCount === 'number' ? raw.savedCount : null,
          error: typeof raw.error === 'string' ? raw.error : null,
          emittedAt: typeof raw.emittedAt === 'string' ? raw.emittedAt : new Date().toISOString(),
        });
        if (status === 'completed' || status === 'failed') {
          this.unwatch(assetId);
        }
      } catch {
        /* ignore */
      }
    });
  }

  private async pollActiveWatchers() {
    if (this.polling || this.watchers.size === 0) {
      return;
    }

    this.polling = true;
    try {
      for (const assetId of [...this.watchers.keys()]) {
        await this.pollWatcher(assetId);
      }
    } finally {
      this.polling = false;
      if (this.watchers.size === 0) {
        this.stopPolling();
      } else {
        this.ensurePolling();
      }
    }
  }

  private finishWatcher(
    watcher: ExtractionWatcher,
    status: AssetParticleExtractionLiveEventStatus,
    extra?: { error?: string | null; savedCount?: number | null },
  ) {
    this.emit({
      kind: 'asset_particle_extraction_state_changed',
      assetId: watcher.assetId,
      userId: watcher.userId || '',
      status,
      taskId: watcher.taskId,
      savedCount: extra?.savedCount ?? null,
      error: extra?.error ?? null,
      emittedAt: new Date().toISOString(),
    });
    this.unwatch(watcher.assetId);
  }

  private async pollWatcher(assetId: string) {
    const watcher = this.watchers.get(assetId);
    if (!watcher) {
      return;
    }

    if (Date.now() >= watcher.expiresAt) {
      this.finishWatcher(watcher, 'failed', { error: '知识颗粒提取等待超时，请稍后重试。' });
      return;
    }

    try {
      if (watcher.taskId) {
        const job = await assetService.getExtractionJob(watcher.taskId);
        watcher.consecutiveFailures = 0;

        if (job.status === 'completed') {
          this.finishWatcher(watcher, 'completed', { savedCount: readSavedCount(job.result) });
          return;
        }

        if (job.status === 'failed' || job.status === 'cancelled') {
          this.finishWatcher(watcher, 'failed', { error: job.error || '知识颗粒提取失败' });
          return;
        }

        return;
      }

      await this.pollWatcherByAssetState(watcher);
    } catch (error) {
      if (watcher.taskId && isJobLookupMiss(error)) {
        watcher.taskId = null;
        try {
          await this.pollWatcherByAssetState(watcher);
          return;
        } catch (fallbackError) {
          const message = fallbackError instanceof Error ? fallbackError.message : '提取状态查询失败';
          this.finishWatcher(watcher, 'failed', { error: message });
          return;
        }
      }

      watcher.consecutiveFailures += 1;
      const message = error instanceof Error ? error.message : '提取状态查询失败';

      if (watcher.consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        try {
          await this.pollWatcherByAssetState(watcher);
          watcher.consecutiveFailures = 0;
          return;
        } catch {
          this.finishWatcher(watcher, 'failed', { error: message });
        }
      }
    }
  }

  private async pollWatcherByAssetState(watcher: ExtractionWatcher) {
    const asset = await assetService.getAsset(watcher.assetId);
    watcher.consecutiveFailures = 0;

    if (isAssetExtractionActive(asset)) {
      return;
    }

    if (isAssetExtractionFailed(asset)) {
      const metadata = asset.metadata || {};
      const nested = metadata.particleExtraction as Record<string, unknown> | undefined;
      const errorMessage = typeof metadata.particleExtractionError === 'string'
        ? metadata.particleExtractionError
        : typeof nested?.error === 'string'
          ? nested.error
          : '知识颗粒提取失败';
      this.finishWatcher(watcher, 'failed', { error: errorMessage });
      return;
    }

    const particles = await assetService.listKnowledgeParticlesByAsset(watcher.assetId);
    this.finishWatcher(watcher, 'completed', { savedCount: particles.length });
  }
}

export const liveUpdatesService = new LiveUpdatesService();
